import { Button } from '@shared/components/ui/button';
import { TextArea } from '@shared/components/ui/textarea';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { faImage, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrdersApiAdapter } from '../../adapters/OrdersApiAdapter';
import { useShipmentStatuses } from '../../hooks/useShipmentStatuses';
import {
  SHIPMENT_STATUS_CORRECTION_ACCEPT_ATTR,
  SHIPMENT_STATUS_CORRECTION_MAX_ATTACHMENTS,
  SHIPMENT_STATUS_CORRECTION_MIN_REASON_LENGTH,
  validateShipmentStatusCorrectionAttachments,
  type ShipmentStatusCorrectionAttachmentErrorKey,
} from '../../utils/shipmentStatusCorrectionAttachments';

interface ShipmentStatusCorrectionModalProps {
  orderId?: number;
  currentShipmentStatusId?: number | null;
  onSuccess?: () => void;
}

export const ShipmentStatusCorrectionModal = forwardRef<ModalCenterRef, ShipmentStatusCorrectionModalProps>(
  ({ orderId, currentShipmentStatusId, onSuccess }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const innerRef = useRef<ModalCenterRef>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const api = new OrdersApiAdapter(createHttpClient(apiClientWH));
    const { shipmentStatuses, loading: shipmentStatusesLoading } = useShipmentStatuses({ finalOnly: true });

    const [shipmentStatusId, setShipmentStatusId] = useState('');
    const [reason, setReason] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
      const urls = attachments.map((file) => URL.createObjectURL(file));
      setPreviewUrls(urls);
      return () => {
        urls.forEach((url) => URL.revokeObjectURL(url));
      };
    }, [attachments]);

    const resetForm = () => {
      const currentId =
        currentShipmentStatusId !== undefined && currentShipmentStatusId !== null
          ? String(currentShipmentStatusId)
          : '';
      const isCurrentFinal = shipmentStatuses.some((status) => String(status.id) === currentId);
      setShipmentStatusId(isCurrentFinal ? currentId : '');
      setReason('');
      setAttachments([]);
    };

    useImperativeHandle(
      ref,
      () => ({
        openModal: () => {
          resetForm();
          innerRef.current?.openModal();
        },
        closeModal: () => {
          innerRef.current?.closeModal();
        },
      }),
      [currentShipmentStatusId, shipmentStatuses],
    );

    const handleClose = () => innerRef.current?.closeModal();

    const showAttachmentError = (errorKey: ShipmentStatusCorrectionAttachmentErrorKey) => {
      toast({
        title: t('shipmentStatusCorrection.attachments.error.title'),
        description: t(`shipmentStatusCorrection.attachments.error.${errorKey}`),
        variant: 'destructive',
      });
    };

    const handleFileSelection = (files: FileList | null) => {
      if (!files?.length) return;

      const merged = [...attachments, ...Array.from(files)].slice(0, SHIPMENT_STATUS_CORRECTION_MAX_ATTACHMENTS);
      const validationError = validateShipmentStatusCorrectionAttachments(merged);

      if (validationError) {
        showAttachmentError(validationError);
        return;
      }

      setAttachments(merged);
    };

    const handleRemoveAttachment = (index: number) => {
      setAttachments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    };

    const handleSave = async () => {
      const trimmedReason = reason.trim();
      if (!orderId || !trimmedReason || !shipmentStatusId) return;

      if (trimmedReason.length < SHIPMENT_STATUS_CORRECTION_MIN_REASON_LENGTH) {
        toast({
          title: t('shipmentStatusCorrection.error.title'),
          description: t('shipmentStatusCorrection.error.reasonTooShort'),
          variant: 'destructive',
        });
        return;
      }

      const attachmentsError = validateShipmentStatusCorrectionAttachments(attachments);
      if (attachmentsError) {
        showAttachmentError(attachmentsError);
        return;
      }

      setIsSubmitting(true);
      try {
        await api.createShipmentStatusCorrection(orderId, {
          shipment_status_id: Number(shipmentStatusId),
          reason: trimmedReason,
          ...(attachments.length > 0 ? { attachments } : {}),
        });
        toast({
          title: t('shipmentStatusCorrection.success.title'),
          description: t('shipmentStatusCorrection.success.description'),
          variant: 'success',
        });
        await queryClient.invalidateQueries({ queryKey: ['shipmentStatusCorrections'] });
        onSuccess?.();
        handleClose();
      } catch (error: any) {
        const msg =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          t('shipmentStatusCorrection.error.description');
        toast({
          title: t('shipmentStatusCorrection.error.title'),
          description: msg,
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    const canSubmit = reason.trim().length >= SHIPMENT_STATUS_CORRECTION_MIN_REASON_LENGTH && !!shipmentStatusId;

    return (
      <ModalCenter
        ref={innerRef}
        title={t('shipmentStatusCorrection.title')}
        width="lg"
        type="default"
        fullScreenMobile={true}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col gap-4 py-4 pb-24 md:pb-4">
            <div>
              <label className="text-sm font-medium">
                {t('shipmentStatusCorrection.shipmentStatus')} <span className="text-error">*</span>
              </label>
              <Select value={shipmentStatusId} onValueChange={setShipmentStatusId} disabled={shipmentStatusesLoading}>
                <SelectTrigger className="w-full mt-1">
                  <SelectValue placeholder={t('shipmentStatusCorrection.shipmentStatusPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {shipmentStatuses.map((status) => (
                    <SelectItem key={status.id} value={String(status.id)}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">
                {t('shipmentStatusCorrection.reason')} <span className="text-error">*</span>
              </label>
              <TextArea
                className="mt-1 min-h-[96px] resize-none"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('shipmentStatusCorrection.reasonPlaceholder')}
              />
              <p className="mt-1 text-xs text-complementary-text">
                {t('shipmentStatusCorrection.reasonHint', {
                  min: SHIPMENT_STATUS_CORRECTION_MIN_REASON_LENGTH,
                })}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">{t('shipmentStatusCorrection.attachments.label')}</label>
              <p className="mt-1 text-xs text-complementary-text">
                {t('shipmentStatusCorrection.attachments.hint', {
                  max: SHIPMENT_STATUS_CORRECTION_MAX_ATTACHMENTS,
                })}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={SHIPMENT_STATUS_CORRECTION_ACCEPT_ATTR}
                multiple
                className="hidden"
                onChange={(e) => {
                  handleFileSelection(e.target.files);
                  e.target.value = '';
                }}
                data-testid="shipment-status-correction-file-input"
              />
              {attachments.length < SHIPMENT_STATUS_CORRECTION_MAX_ATTACHMENTS && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faImage} className="mr-2 !w-3.5 !h-3.5" />
                  {t('shipmentStatusCorrection.attachments.add')}
                </Button>
              )}
              {attachments.length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {attachments.map((file, index) => (
                    <div
                      key={`${file.name}-${file.lastModified}-${index}`}
                      className="relative rounded-md border border-card-border overflow-hidden"
                    >
                      <img src={previewUrls[index]} alt={file.name} className="h-24 w-full object-cover" />
                      <button
                        type="button"
                        className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-background/90 text-foreground"
                        onClick={() => handleRemoveAttachment(index)}
                        aria-label={t('shipmentStatusCorrection.attachments.remove')}
                      >
                        <FontAwesomeIcon icon={faXmark} className="!w-3 !h-3" />
                      </button>
                      <p className="truncate px-2 py-1 text-xs text-complementary-text">{file.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="lg" onClick={handleClose}>
                {t('shipmentStatusCorrection.cancel')}
              </Button>
              <Button variant="primary" size="lg" onClick={handleSave} disabled={isSubmitting || !canSubmit}>
                {isSubmitting ? t('shipmentStatusCorrection.submitting') : t('shipmentStatusCorrection.save')}
              </Button>
            </div>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

ShipmentStatusCorrectionModal.displayName = 'ShipmentStatusCorrectionModal';
